import { useDraggable } from '@dnd-kit/core';
import { Link, useLocation } from 'react-router-dom';
import { LucideIcon, Star } from 'lucide-react';
import { SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';

interface DraggableMenuItemProps {
  id: string;
  url: string;
  title: string;
  icon: LucideIcon;
  color: string;
  isFavorited?: boolean;
}

export function DraggableMenuItem({ id, url, title, icon: Icon, color, isFavorited = false }: DraggableMenuItemProps) {
  const location = useLocation();
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id });

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <SidebarMenuItem ref={setNodeRef}>
      <SidebarMenuButton
        asChild
        className={cn(
          'group relative rounded-lg transition-all duration-200',
          isActive(url)
            ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
            : 'hover:bg-sidebar-accent/50',
          isDragging && 'opacity-50',
          isFavorited && 'opacity-60'
        )}
      >
        <Link to={url} className="flex items-center gap-3 px-3 py-2">
          <div
            {...listeners}
            {...attributes}
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-md text-white transition-transform group-hover:scale-110 cursor-grab active:cursor-grabbing',
              color
            )}
          >
            <Icon className="h-4 w-4" />
          </div>
          <span className="text-sm flex-1">{title}</span>
          {isFavorited && (
            <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500 flex-shrink-0" />
          )}
          {isActive(url) && (
            <div className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 bg-primary rounded-r-full" />
          )}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}
